/**
 * Centralized CMS API Utility
 * Handles authentication, 401 errors, and provides consistent API calls across all CMS pages
 */

const API_URL = window.location.origin;

// Event for auth state changes (logout on 401)
const authEvents = new EventTarget();

export const AUTH_EVENTS = {
  UNAUTHORIZED: 'cms:unauthorized',
  SESSION_EXPIRED: 'cms:session_expired'
};

/**
 * Get the current CMS token from localStorage
 */
const getToken = () => {
  return localStorage.getItem('cms_token');
};

/**
 * Clear auth data and dispatch logout event
 */
const handleUnauthorized = () => {
  localStorage.removeItem('cms_token');
  localStorage.removeItem('cms_user');
  localStorage.removeItem('cms_permissions');
  
  // Dispatch event for components to handle
  authEvents.dispatchEvent(new CustomEvent(AUTH_EVENTS.UNAUTHORIZED));
  
  // Redirect to login
  if (window.location.pathname.startsWith('/admin') && !window.location.pathname.includes('/login')) {
    window.location.href = '/admin';
  }
};

/**
 * Subscribe to auth events
 */
export const onAuthEvent = (event, callback) => {
  authEvents.addEventListener(event, callback);
  return () => authEvents.removeEventListener(event, callback);
};

/**
 * Make an authenticated CMS API request
 * Automatically handles 401 errors and redirects to login
 * 
 * @param {string} endpoint - API endpoint (e.g., '/api/cms/site-settings/testimonials')
 * @param {object} options - Fetch options (method, body, headers, etc.)
 * @returns {Promise<Response>} - Fetch response
 */
export const cmsApiFetch = async (endpoint, options = {}) => {
  const token = getToken();
  
  const headers = {
    ...options.headers,
  };
  
  // Add auth header if we have a token
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  // Add content-type for JSON bodies
  if (options.body && typeof options.body === 'string') {
    headers['Content-Type'] = headers['Content-Type'] || 'application/json';
  }
  
  const url = endpoint.startsWith('http') ? endpoint : `${API_URL}${endpoint}`;
  
  try {
    const response = await fetch(url, {
      ...options,
      headers
    });
    
    // Handle 401 Unauthorized
    if (response.status === 401) {
      console.warn('CMS API: Unauthorized - redirecting to login');
      handleUnauthorized();
      throw new Error('Session expired. Please log in again.');
    }
    
    return response;
  } catch (error) {
    // Re-throw auth errors
    if (error.message.includes('Session expired')) {
      throw error;
    }
    // Network errors
    console.error('CMS API Error:', error);
    throw error;
  }
};

/**
 * Convenience methods for common HTTP verbs
 */
export const cmsApi = {
  get: (endpoint, options = {}) => 
    cmsApiFetch(endpoint, { ...options, method: 'GET' }),
  
  post: (endpoint, data, options = {}) => 
    cmsApiFetch(endpoint, { 
      ...options, 
      method: 'POST',
      body: typeof data === 'string' ? data : JSON.stringify(data)
    }),
  
  put: (endpoint, data, options = {}) => 
    cmsApiFetch(endpoint, { 
      ...options, 
      method: 'PUT',
      body: typeof data === 'string' ? data : JSON.stringify(data)
    }),
  
  patch: (endpoint, data, options = {}) => 
    cmsApiFetch(endpoint, { 
      ...options, 
      method: 'PATCH',
      body: typeof data === 'string' ? data : JSON.stringify(data)
    }),
  
  delete: (endpoint, options = {}) => 
    cmsApiFetch(endpoint, { ...options, method: 'DELETE' }),
  
  // For file uploads (FormData)
  upload: (endpoint, formData, options = {}) =>
    cmsApiFetch(endpoint, {
      ...options,
      method: 'POST',
      body: formData,
      // Don't set Content-Type - let browser set it with boundary
      headers: { ...options.headers }
    })
};

/**
 * Helper to parse JSON response with error handling
 */
export const parseJsonResponse = async (response) => {
  try {
    return await response.json();
  } catch {
    return null;
  }
};

/**
 * Helper for common pattern: fetch, check ok, parse JSON
 */
export const fetchJson = async (endpoint, options = {}) => {
  const response = await cmsApiFetch(endpoint, options);
  if (!response.ok) {
    const error = await parseJsonResponse(response);
    throw new Error(error?.detail || `Request failed: ${response.status}`);
  }
  return parseJsonResponse(response);
};

export default cmsApi;
